import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import MovieIcon from '@mui/icons-material/Movie';
import ImageIcon from '@mui/icons-material/Image';
import GifBoxIcon from '@mui/icons-material/GifBox';
import { signageService } from '../../services/signageService';
import { SignageMediaType } from '../../types/signage';

// Uploaded files are served behind the bearer token, so they are fetched as
// blobs and shown through object URLs (cached for the session).
const fileUrlCache = new Map<string, Promise<string>>();

const loadFileUrl = (fileId: string): Promise<string> => {
  let pending = fileUrlCache.get(fileId);
  if (!pending) {
    pending = signageService.getFileBlob(fileId).then((blob) => URL.createObjectURL(blob));
    pending.catch(() => fileUrlCache.delete(fileId));
    fileUrlCache.set(fileId, pending);
  }
  return pending;
};

export const forgetFileUrl = (fileId: string) => fileUrlCache.delete(fileId);

// Resolves the browser-loadable address of an item's media (or undefined while
// it loads or if it cannot be loaded).
export const useMediaSrc = (fileId?: string, sourceUrl?: string): string | undefined => {
  const [fileSrc, setFileSrc] = useState<string>();

  useEffect(() => {
    let cancelled = false;
    setFileSrc(undefined);
    if (fileId) {
      loadFileUrl(fileId)
        .then((url) => !cancelled && setFileSrc(url))
        .catch(() => undefined);
    }
    return () => {
      cancelled = true;
    };
  }, [fileId]);

  return fileId ? fileSrc : sourceUrl;
};

const typeIcon = (mediaType: SignageMediaType) =>
  mediaType === 'video' ? <MovieIcon fontSize="small" /> : mediaType === 'gif' ? <GifBoxIcon fontSize="small" /> : <ImageIcon fontSize="small" />;

interface MediaThumbProps {
  mediaType: SignageMediaType;
  fileId?: string;
  sourceUrl?: string;
  width: number | string;
  height: number | string;
  dimmed?: boolean;
}

const MediaThumb: React.FC<MediaThumbProps> = ({ mediaType, fileId, sourceUrl, width, height, dimmed }) => {
  const src = useMediaSrc(fileId, sourceUrl);
  const [failed, setFailed] = useState(false);

  useEffect(() => setFailed(false), [src]);

  return (
    <Box
      sx={{
        width,
        height,
        flex: 'none',
        borderRadius: 1,
        overflow: 'hidden',
        bgcolor: 'action.hover',
        display: 'grid',
        placeItems: 'center',
        color: 'text.secondary',
        opacity: dimmed ? 0.45 : 1,
        '& img, & video': { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
      }}
    >
      {src && !failed ? (
        mediaType === 'video' ? (
          <video src={src} muted preload="metadata" playsInline onError={() => setFailed(true)} />
        ) : (
          <img src={src} alt="" onError={() => setFailed(true)} />
        )
      ) : (
        typeIcon(mediaType)
      )}
    </Box>
  );
};

export default MediaThumb;
